import { useState, type FormEvent } from 'react'
import { Button, Field, Input, Textarea } from '@/shared/ui'
import { formatDateTime } from '@/shared/lib/time'
import type { Profile, ProfileResponse } from '@/shared/api/types'
import { useSaveProfile } from '../model'
import styles from './ProfileForm.module.css'

interface ProfileFormProps {
  initial: ProfileResponse
}

/**
 * Анкета компании — то, из чего собирается речь агента.
 *
 * Поля разбиты по смыслу и подписаны примерами. Причина в том, что
 * заполняет её не автор промптов, а владелец бизнеса: на пустое поле
 * «Расскажите о компании» он напишет три слова, а на вопрос с примером —
 * то, что агенту действительно нужно знать.
 */
export function ProfileForm({ initial }: ProfileFormProps) {
  const [form, setForm] = useState<Profile>(strip(initial))
  const save = useSaveProfile()

  // Правки есть — сравниваем с тем, что пришло с сервера. Так кнопка
  // «Сохранить» не предлагает сохранить нетронутую анкету, а человек
  // видит, что изменения ещё не ушли.
  const dirty = JSON.stringify(form) !== JSON.stringify(strip(initial))

  function set<K extends keyof Profile>(key: K, value: Profile[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    save.mutate(form)
  }

  return (
    <form className={styles.form} onSubmit={submit}>
      <div className={styles.group}>
        <div className={styles.row}>
          <Field
            label="Имя агента"
            hint="Так он представится в начале звонка"
          >
            {(props) => (
              <Input
                {...props}
                placeholder="Илья"
                value={form.agentName}
                onChange={(e) => set('agentName', e.target.value)}
              />
            )}
          </Field>

          <Field label="Название для звонка" hint="«…компания Синта»">
            {(props) => (
              <Input
                {...props}
                placeholder="Синта"
                value={form.companyLegal}
                onChange={(e) => set('companyLegal', e.target.value)}
              />
            )}
          </Field>
        </div>

        <Field
          label="Чем занимается компания"
          hint="Одним-двумя предложениями, как объяснили бы знакомому"
        >
          {(props) => (
            <Textarea
              {...props}
              limit={600}
              rows={3}
              placeholder="Делаем голосовых агентов для холодных звонков: агент сам звонит по базе, ведёт диалог и передаёт тёплых менеджеру."
              value={form.business}
              onChange={(e) => set('business', e.target.value)}
            />
          )}
        </Field>

        <Field
          label="Что предлагаем"
          hint="Продукты и услуги. Агент будет опираться на этот список"
        >
          {(props) => (
            <Textarea
              {...props}
              limit={1200}
              rows={4}
              placeholder={'— Обзвон базы: агент звонит и квалифицирует\n— Приём входящих\n— Интеграция с вашей CRM'}
              value={form.offerings}
              onChange={(e) => set('offerings', e.target.value)}
            />
          )}
        </Field>

        <Field
          label="Цены и условия"
          hint="Что агенту можно называть вслух. Пусто — цены он обсуждать не станет"
        >
          {(props) => (
            <Textarea
              {...props}
              limit={800}
              rows={3}
              placeholder="От 30 000 ₽ в месяц. Первая неделя бесплатно."
              value={form.pricing}
              onChange={(e) => set('pricing', e.target.value)}
            />
          )}
        </Field>

        <Field
          label="Чего обещать нельзя"
          hint="Скидки, сроки, гарантии — то, чего агент не даст, как бы его ни уговаривали"
        >
          {(props) => (
            <Textarea
              {...props}
              limit={600}
              rows={3}
              placeholder="Не обещать скидок больше 10%. Не называть сроков внедрения меньше двух недель."
              value={form.forbidden}
              onChange={(e) => set('forbidden', e.target.value)}
            />
          )}
        </Field>

        <Field
          label="Цель звонка"
          hint="Чем должен закончиться удачный разговор"
        >
          {(props) => (
            <Textarea
              {...props}
              limit={400}
              rows={2}
              placeholder="Договориться о двадцатиминутном показе на этой неделе."
              value={form.goal}
              onChange={(e) => set('goal', e.target.value)}
            />
          )}
        </Field>

        <Field
          label="Дополнительные указания"
          hint="Необязательно. Добавится к промпту в конце и не отменит правил речи"
        >
          {(props) => (
            <Textarea
              {...props}
              limit={1500}
              rows={3}
              placeholder="Если спросят про интеграцию с Битрикс — говорить, что она есть."
              value={form.extraPrompt}
              onChange={(e) => set('extraPrompt', e.target.value)}
            />
          )}
        </Field>
      </div>

      <div className={styles.actions}>
        <Button type="submit" loading={save.isPending} disabled={!dirty}>
          Сохранить
        </Button>
        {dirty ? (
          <span className={styles.dirty}>Есть несохранённые правки</span>
        ) : (
          <span className={styles.saved}>Сохранено {formatDateTime(initial.updatedAt)}</span>
        )}
      </div>

      {/* Предупреждение под формой, а не в подсказке поля: оно про всю
          анкету сразу, и человек должен увидеть его до того, как впишет
          в «дополнительные указания» пароль от CRM. */}
      <p className={styles.notice}>
        Всё, что здесь написано, агент может произнести вслух. Не указывайте пароли,
        ключи доступа и данные ваших клиентов.
      </p>
    </form>
  )
}

/** Отбрасывает служебные поля ответа: наружу уходит только анкета. */
function strip(response: ProfileResponse): Profile {
  return {
    agentName: response.agentName,
    companyLegal: response.companyLegal,
    business: response.business,
    offerings: response.offerings,
    pricing: response.pricing,
    forbidden: response.forbidden,
    goal: response.goal,
    extraPrompt: response.extraPrompt,
  }
}
